<template>
  <div class="logs-container">
    <!-- Header Section -->
    <div class="header">
      <a-icon type="LeftArrow" @click="goBack" class="icon" />
      <div class="header-content">
        <div class="dashboard">Dashboard /</div>
        <b>Cash Collection Logs</b>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="filters">
      <b>Transaction List</b>
      <div class="dropdown-container">
        <Dropdown
          :alloptions="filterOptions"
          :selectedvalue="selectedFilter"
          @select="updateFilter"
          defaultValue="momth"
          size="large"
          :validateStatus="validateStatus.filter"
        />
      </div>
    </div>

    <!-- Table Section -->

    <div class="table-container" v-if="logs.length > 0">
      <ATable
        :rows="logs"
        :columns="tableColumns"
        :pagelength="fetchSize"
        :pagecount="totalPages"
        :sortorder="'asc'"
        :sortby="'transactionDate'"
        :autoheight="true"
        :showTableControls="true"
        containerbordertype="noshadow"
      />
    </div>
    <div class="table-container" v-if="logs.length == 0">
      No Logs Available for this Time Period
    </div>

    <!-- Pagination Section -->
    <div class="pagination">
      <a-button
        v-for="page in totalPages"
        :key="page"
        @click="goToPage(page)"
        :disabled="currentPage === page"
      >
        {{ page }}
      </a-button>
    </div>
    <div class="btn-container" v-if="totalPages">
      <button
        v-for="page in totalPages"
        :key="page"
        :class="['pagination-btn', { active: pagination.currentPage === page }]"
        @click="handlePageChange(page - 1)"
      >
        {{ page }}
      </button>
    </div>
  </div>
</template>

<script>
import { ATable } from "@canvas/componentlibrary";
import { get } from "@/api";
import store from "../../store";
import { getDateRange } from "@/utils";
import { mapState } from "vuex";

import CellRenderer from "../TableRenderers/NameRenderer.vue";

export default {
  components: {
    ATable,
  },
  name: "CashDocumentsViewLogs",
  data() {
    return {
      id: null,
      logs: [],
      currentPage: 0,
      fetchSize: 10, // Number of items per page
      totalCount: 0, // Total count of records from the API
      filterOptions: [
        { name: "today", display_name: "Today" },
        { name: "week", display_name: "This Week" },
        { name: "month", display_name: "This Month" },
        {
          name: "year",
          display_name: "This Year",
        },
      ],
      tableColumns: [
        {
          name: "transactionDate",
          display_name: "Transaction Date",
          sortable: false,
          width: 25,
          height: 100,
          cellrenderer: CellRenderer,
        },
        {
          name: "totalCredit",
          display_name: "Cash Collected",
          sortable: false,
          width: 25,
          height: 100,
          cellrenderer: CellRenderer,
        },
        {
          name: "totalDebit",
          display_name: "DEPOSITED",
          sortable: false,
          width: 25,
          height: 100,
          cellrenderer: CellRenderer,
        },
        {
          name: "amount",
          display_name: "In Hand",
          sortable: false,
          width: 25,
          height: 100,
          cellrenderer: CellRenderer,
        },
      ],
      selectedFilter: "month",
      validateStatus: {
        filter: "",
      },
    };
  },
  computed: {
    ...mapState("appModule", {
      selectedEc: "selectedEc",
    }),
    totalPages() {
      return Math.ceil(this.totalCount / this.fetchSize);
    },
  },
  created() {
    this.id = this.$route.params.id;
    this.fetchLogs();
  },
  mounted() {
    this.selectedFilter = { name: "month", display_name: "This Month" };
  },
  methods: {
    formatDate(date) {
      const d = new Date(date);
      const day = d.getDate();
      const month = d.toLocaleString("default", { month: "short" });
      const year = d.getFullYear();
      return `${day} ${month} ${year}`;
    },
    goBack() {
      this.$router.go(-1);
    },
    updateFilter(value = "month") {
      this.selectedFilter = value;
      this.currentPage = 0; // Reset to the first page
      this.fetchLogs();
    },
    fetchLogs() {
      this.$store.commit("uiModule/showLoadingModal", null, { root: true });
      const dateRange = getDateRange(this.selectedFilter);

      const query = [
        `startDate=${dateRange.start}`,
        `endDate=${dateRange.end}`,
        `pageNumber=${this.currentPage}`,
        `pageSize=${this.fetchSize}`,
        "transferType=debit",
      ].join("&");
      get({
        endpoint:
          "gringotts.api.v1.transactions.wallets.wallet-summary-details",
        query: query,
        cash_wallet_id: store.state.appModule.selectedEc.cash_wallet_id,
      })
        .then((response) => {
          this.logs = response.walletDTOList?.map((log) => ({
            transactionDate: this.formatDate(log.transactionDate),
            totalCredit: log.totalCredit || 0,
            totalDebit: log.totalDebit || 0,
            amount: log.balance || 0,
          }));
          this.totalCount = response.totalCount || 0; // Update total count
        })
        .catch((err) => {
          // TODO
          /** Handle this */
          /** Show message to user if it is required */
          // console.error("Error fetching logs:", err);
        })
        .finally(() => {
          this.$store.commit("uiModule/hideLoadingModal", null, { root: true });
        });
    },

    goToPage(page) {
      if (this.currentPage !== page) {
        this.currentPage = page;
        this.fetchLogs();
      }
    },
  },
  watch: {
    selectedEc() {
      // Reload logs when EC changes
      this.currentPage = 0;
      this.fetchLogs();
    },
  },
};
</script>

<style scoped>
.logs-container {
  background-color: #f2f2f2;
}
.header {
  background-color: white;
  padding: 20px;
  display: flex;
  border-bottom: 1px solid rgb(231, 225, 225);
}
.header-content {
  margin-left: 16px;
}
.filters {
  padding: 20px 24px;
  background-color: #f8f9fa;
  display: flex;
  justify-content: space-between;
}
.dropdown-container {
  min-width: 220px;
}
.table-container {
  padding: 24px;
  min-height: 75vh;
}
.log-table {
  width: 100%;
  background-color: #fff;
  border-collapse: collapse;
}
.log-table th,
.log-table td {
  padding: 12px;
  text-align: left;
}
.log-table th {
  border-bottom: 1px solid rgb(201, 225, 225);
}
.log-table tr {
  border-bottom: 1px solid rgb(231, 225, 225);
}
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
.pagination a-button {
  margin: 0 5px;
}
.icon {
  cursor: pointer;
}
.dashboard {
  font-size: 12px;
  color: gray;
}
</style>
